import classes from './Comments.module.less'
import TextArea from 'antd/lib/input/TextArea'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ClassItem, CommentsType } from 'Types/Classes.types'
import Dropdown from 'antd/lib/dropdown'
import Button from 'antd/lib/button'
import MoreOutlined from '@ant-design/icons/lib/icons/MoreOutlined'
import Flex from 'antd/lib/flex'
import Divider from 'antd/lib/divider'
import { EmptyStateIndicator } from 'Components/common/EmptyStaleIndicator/EmptyStaleIndicator'
import EmptyCommentsIndicator from 'Assets/icons/empty-comments.svg'
import Space from 'antd/lib/space'

export type CommentsBodyPropsType = {
    classes: ClassItem[]
    comments: CommentsType[]
    updateAnnotationComment: (annotationId: string, comment: string) => void
    deleteAnnotationComment: (annotationId: string) => void
    focusedAnnotationId: string | null
    setFocusedAnnotationId: (id: string | null) => void
    onClickComment: (id: string) => void
    isReadOnly: boolean
}

export const CommentsBody = (props: CommentsBodyPropsType) => {
    const [commentsList, setCommentsList] = useState<CommentsType[]>([])

    useEffect(() => {
        setCommentsList(props.comments)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(props.comments)])

    const onUpdateComment = useCallback((index: number, value: string) => {
        setCommentsList(prevCommentsList => {
            const newCommentsList = [...prevCommentsList]
            newCommentsList[index].content = value
            return newCommentsList
        })
    }, [])

    const onClickEditIcon = useCallback((index: number) => {
        setCommentsList(prevCommentsList => {
            const newCommentsList = [...prevCommentsList]
            newCommentsList[index].isEditable = true
            return newCommentsList
        })
    }, [])

    const onClickCancelUpdateComment = useCallback((index: number) => {
        setCommentsList(prevCommentsList => {
            const newCommentsList = [...prevCommentsList]
            newCommentsList[index].content = newCommentsList[index].originalContent
            newCommentsList[index].isEditable = false
            return newCommentsList
        })
    }, [])

    const onClickSaveUpdateComment = useCallback(
        (index: number) => {
            setCommentsList(prevCommentsList => {
                const newCommentsList = [...prevCommentsList]
                newCommentsList[index].originalContent = newCommentsList[index].content
                newCommentsList[index].isEditable = false
                return newCommentsList
            })

            props.updateAnnotationComment(
                commentsList[index].annotationId,
                commentsList[index].content
            )
        },
        [commentsList, props]
    )

    const onClickDeleteIcon = useCallback(
        (index: number) => {
            props.deleteAnnotationComment(commentsList[index].annotationId)
            setCommentsList(prevCommentsList => {
                const newCommentsList = [...prevCommentsList]
                newCommentsList.splice(index, 1)
                return newCommentsList
            })
        },
        [commentsList, props]
    )

    const emptyContent = useMemo(() => {
        return <EmptyStateIndicator caption={'No comments found'} icon={EmptyCommentsIndicator} />
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(props.comments)])

    const getcommentsContent = (comment: CommentsType, index: number) => {
        if (comment.isEditable) {
            return (
                <>
                    <TextArea
                        value={comment.content}
                        autoSize
                        onChange={e => onUpdateComment(index, e.target.value)}
                    />
                    <Flex justify="flex-end" gap={8}>
                        <Button
                            className={classes['comment-cancel-button']}
                            onClick={() => onClickCancelUpdateComment(index)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            className={classes['comment-save-button']}
                            onClick={() => onClickSaveUpdateComment(index)}
                        >
                            Save
                        </Button>
                    </Flex>
                </>
            )
        } else {
            return <>{comment.content}</>
        }
    }

    return (
        <div className={classes['comments-body']}>
            {commentsList.length === 0 && emptyContent}
            {commentsList.map((comment, index) => (
                <Space
                    className={
                        classes['comment-container'] +
                        ' ' +
                        (comment.annotationId === props.focusedAnnotationId
                            ? classes['focused']
                            : '')
                    }
                    key={comment.annotationId}
                    onClick={() => props.onClickComment(comment.annotationId)}
                    onMouseEnter={() => props.setFocusedAnnotationId(comment.annotationId)}
                    onMouseLeave={() => props.setFocusedAnnotationId(null)}
                    data-testid="comment-container"
                >
                    <div className={classes['comment-header']}>
                        <div
                            className={classes['comment-header-icon']}
                            style={{ background: comment.classItem.color }}
                        ></div>
                        <div className={classes['comment-header-title']}>
                            {comment.classItem.label}
                        </div>
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: 'edit',
                                        label: 'Edit',
                                        onClick: () => onClickEditIcon(index),
                                        disabled: props.isReadOnly,
                                    },
                                    {
                                        key: 'delete',
                                        label: 'Delete',
                                        onClick: () => onClickDeleteIcon(index),
                                        disabled: props.isReadOnly,
                                    },
                                ],
                            }}
                            trigger={['click']}
                            placement="bottomRight"
                            className={classes['comment-header-dropdown']}
                        >
                            <MoreOutlined />
                        </Dropdown>
                    </div>
                    <Divider className={classes['comment-divider']} />
                    <div className={classes['comment-content']}>
                        {getcommentsContent(comment, index)}
                    </div>
                </Space>
            ))}
        </div>
    )
}
